import { useEffect, useRef, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { theme } from "../../../theme";
import { HorizontalSpacer } from "../../atoms/Spacers";
import Card from "../../atoms/Card";
import { Row } from "react-grid-system";
import OutlinedButton from "../../atoms/buttons/OutlinedButton";
import FilterChip from "../../atoms/inputs/FilterChip";
import AddTagDropdown from "../../molecules/AddTagDropdown";
import ActionButton from "../../atoms/buttons/ActionButton";
import { RefreshIcon } from "../../atoms/icons/RefreshIcon";
import BodySmall from "../../atoms/typography/BodySmall";
import ToggleButton from "../../atoms/buttons/ToggleButton";
import { GridIcon } from "../../atoms/icons/GridIcon";
import { ListIconView } from "../../atoms/icons/ListIconView";
import InformationDetailedColumn from "../../organisms/validation/InformationDetailedColumn";
import InvoiceDetailsColumn from "../../organisms/validation/InvoiceDetailsColumn";
import InvoiceViewColumn from "../../organisms/validation/InvoiceViewCoulmn";
import Modal from "../../molecules/Modal";
import {
  CheckedStatusCircle,
  FailedStatusCircle,
  WarningStatusCircle,
} from "../../atoms/StatusCircles";
import Body from "../../atoms/typography/Body";
import XS from "../../atoms/typography/XS";
import CircleIcon from "../../atoms/CircleIcon";
import { CheckItemContainer } from "../../molecules/invoices/InvoiceValidationResult";
import type { InvoiceTag } from "../../../models/InvoiceTags";
import { useSetInvoiceTags } from "../../../graphql/hooks/invoices/useSetInvoiceTags";
import SmallLoadingSpinner from "../../atoms/SmallLoadingSpinner";
import Alert from "../../molecules/Alert";
import { useValidateInvoice } from "../../../graphql/hooks/invoices/useValidateInvoice";
import { useValidateManually } from "../../../graphql/hooks/invoices/useValidateManually";
import RejectInvoicesButton from "../../molecules/invoices/RejectInvoicesButton";
import { SendIcon } from "../../atoms/icons/SendIcon";
import { useInvoiceLatestValidationResult } from "../../../graphql/hooks/invoices/useInvoiceLatestValidationResult";
import LoadingSpinner from "../../atoms/LoadingSpinner";
import DeleteInvoicesButton from "../../molecules/invoices/DeleteInvoicesButton";
import { useClient } from "../../../graphql/hooks/useClient";
import { InvoiceAssessmentRoute } from "../../../utils/routes";
import { useSendInvoiceToAssessment } from "../../../graphql/hooks/invoices/useSendInvoiceToAssessment";
import SecondaryPageTitleSection from "../../molecules/SecondaryPageTitleSection";
import ValidationActionBottomBar from "../../organisms/validation/ValidationActionBottomBar";
import { SaveSectionsProvider } from "../../organisms/validation/SaveSectionsContext";
import styled from "styled-components";
import TextTag from "../../atoms/TextTag";
import DuplicatedInvoiceNumberListModalContent from "../../molecules/modals/invoices/DuplicatedInvoiceNumberListModalContent";
import SendValidationInvoiceToAssessmentWithDupesModal from "../../molecules/modals/invoices/SendValidationInvoiceToAssessmentWithDupesModal";
import TextButton from "../../atoms/buttons/TextButton";
import { OpenLinkIconSmall } from "../../atoms/icons/OpenLinkIcon";
import SendInvoiceMessageToClientButton from "../../molecules/invoices/SendInvoiceMessageToClientButton";
import CopyForDebuggingButton from "../../molecules/CopyForDebuggingButton";

const DupeButtonContainer = styled.button`
  border: none;
  background-color: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;

  &:hover {
    filter: opacity(0.6);
  }
`;

const InvoiceValidationDetailedPage: FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { id, clientIdentifier } = useParams();
  const navigate = useNavigate();
  const from = location.state?.from || "/";

  const { invoice, error, loading } = useInvoiceLatestValidationResult(
    clientIdentifier ?? "",
    id || "",
  );

  const invoiceProcessStatusIsInValidationStatus =
    invoice?.state?.processStatus === "VALIDATION";

  const { client, error: clientError } = useClient(clientIdentifier ?? "");

  const [tags, setTags] = useState<Array<InvoiceTag>>([]);

  const containerRef = useRef<HTMLDivElement>(null);
  const prevWidthRef = useRef<number | null>(null);
  const [isGridView, setIsGridView] = useState(true);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [validationPassed, setValidationPassed] = useState(false);
  const [showApproveWarningsModal, setShowApproveWarningsModal] =
    useState(false);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [sendToAssessmentWithDupesOpen, setSendToAssessmentsWithDupesOpen] =
    useState(false);

  const [invoiceHasBeenSentToAssessment, setInvoiceHasBeenSentToAssessment] =
    useState(false);

  const [duplicatedModalOpen, setDuplicatedModalOpen] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width;
      const prev = prevWidthRef.current;
      if (prev === null) {
        setIsGridView(width >= 1400);
      } else if (prev >= 1400 && width < 1400) {
        setIsGridView(false);
      } else if (prev < 1400 && width >= 1400) {
        setIsGridView(true);
      }
      prevWidthRef.current = width;
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (invoice?.tags) {
      setTags(invoice.tags.map((t) => t.tag));
    }
  }, [invoice]);

  const onCompletedUpdateTags = (res?: any) => {
    if (res?.invoice?.setInvoiceTags?.success !== true) {
      setErrorMessage(t("invoices.updateTagsError"));
    }
  };
  const onErrorUpdateTags = (e?: any) => {
    console.error("Error updating tags", e);
    setErrorMessage(t("invoices.updateTagsError"));
  };
  const {
    setInvoiceTags,
    error: setTagsError,
    loading: setTagsLoading,
  } = useSetInvoiceTags({
    onCompleted: onCompletedUpdateTags,
    onError: onErrorUpdateTags,
  });
  const onToggleTag = async (tag: InvoiceTag, add: boolean) => {
    let newTags: InvoiceTag[];
    if (add) {
      setTags([...tags, tag]);
      newTags = [...tags, tag];
    } else {
      setTags(tags.filter((t) => t !== tag));
      newTags = tags.filter((t) => t !== tag);
    }

    try {
      await setInvoiceTags({
        variables: {
          input: {
            invoiceIds: [id],
            tags: newTags,
          },
        },
      });
    } catch (e) {
      console.error("Error updating tags", e);
      setErrorMessage(t("invoices.updateTagsError"));
    }
  };

  const onApplyTags = async (add: string[], remove: string[]) => {
    const newTags = [
      ...tags.filter((t) => !remove.includes(t)),
      ...add.filter((a) => !tags.includes(a as InvoiceTag)),
    ] as InvoiceTag[];
    setTags(newTags);
    await setInvoiceTags({
      variables: {
        input: {
          invoiceIds: [id],
          tags: newTags,
        },
      },
    });
  };

  const onCompletedValidateInvoice = (res?: any) => {
    if (res?.invoice?.validateInvoice?.isValid) {
      setValidationPassed(true);
    } else {
      setValidationPassed(false);
    }
    console.log("Invoice validated successfully", res);
    setShowConfirmationModal(true);
  };
  const onErrorValidateInvoice = (e?: any) => {
    console.error("Error validating invoice", e);
    setErrorMessage(t("invoiceValidation.revalidationFailed"));
  };
  const {
    validateInvoice,
    loading: validateInvoiceLoading,
    error: validateInvoiceError,
  } = useValidateInvoice({
    onCompleted: onCompletedValidateInvoice,
    onError: onErrorValidateInvoice,
  });
  const onClickRevalidate = async () => {
    try {
      await validateInvoice({
        variables: {
          input: {
            invoiceId: id,
          },
          clientIdentifier: clientIdentifier,
        },
      });
    } catch (e) {
      console.error("Error validating invoice", e);
      setErrorMessage(t("invoiceValidation.revalidationFailed"));
    }
  };

  const onCompletedSendToAssessment = (res?: any) => {
    if (res?.invoice?.sendInvoiceToAssessment?.success === true) {
      setInvoiceHasBeenSentToAssessment(true);
      setValidationPassed(true);
      setShowConfirmationModal(true);
    } else {
      setErrorMessage(t("invoiceValidation.sendToAssessmentFailed"));
    }
  };
  const onErrorSendToAssessment = (e?: any) => {
    console.error("Error sending invoice to assessment", e);
    setErrorMessage(t("invoiceValidation.sendToAssessmentFailed"));
  };
  const {
    sendInvoiceToAssessment,
    loading: sendToAssessmentLoading,
    error: sendToAssessmentError,
  } = useSendInvoiceToAssessment({
    onCompleted: onCompletedSendToAssessment,
    onError: onErrorSendToAssessment,
  });
  const onClickSendToAssessment = async () => {
    try {
      await sendInvoiceToAssessment({
        variables: {
          input: {
            invoiceId: id,
          },
          clientIdentifier: clientIdentifier,
        },
      });
    } catch (e) {
      console.error("Error sending invoice to assessment", e);
      setErrorMessage(t("invoiceValidation.sendToAssessmentFailed"));
    }
  };

  const onCompletedValidateManually = (res?: any) => {
    setShowApproveWarningsModal(false);
    if (res?.invoice?.validateManually?.success === true) {
      setInvoiceHasBeenSentToAssessment(true);
      setValidationPassed(true);
      setShowConfirmationModal(true);
    } else {
      setErrorMessage(t("invoiceValidation.manualValidationFailed"));
    }
  };
  const onErrorValidateManually = (e?: any) => {
    console.error("Error manually validating invoice", e);
    setShowApproveWarningsModal(false);
    setErrorMessage(t("invoiceValidation.manualValidationFailed"));
  };
  const {
    validateManually,
    loading: validateManuallyLoading,
    error: validateManuallyError,
  } = useValidateManually({
    onCompleted: onCompletedValidateManually,
    onError: onErrorValidateManually,
  });
  const onClickApproveWarnings = async () => {
    try {
      await validateManually({
        variables: {
          input: {
            invoiceId: id,
          },
        },
      });
    } catch (e) {
      console.error("Error manually validating invoice", e);
      setShowApproveWarningsModal(false);
      setErrorMessage(t("invoiceValidation.manualValidationFailed"));
    }
  };

  const revalidationResultIsWarningsOnly =
    !validationPassed &&
    !invoiceHasBeenSentToAssessment &&
    invoice?.state?.validationStatus === "VALIDATED_WITH_WARNINGS";

  // The validate mutation only returns isValid; the refetched latestValidation holds the reasons.
  const latestValidation = invoice?.latestValidation;
  const failedValidationItems = [
    ...(latestValidation?.fieldValidations ?? [])
      .filter((field) => !field.isValid)
      .map((field) => ({
        title: t("invoices.fieldIsInvalid", {
          fieldName: t(`invoices.${field.fieldType}`, {
            defaultValue: field.fieldType,
          }),
        }),
        message: field.value,
      })),
    ...(latestValidation?.rowValidations ?? [])
      .flatMap((row) => row.rowFieldValidations)
      .filter((field) => !field.isValid)
      .map((field) => ({
        title: t("invoices.rowIsInvalid", {
          rowName: t(`invoices.${field.fieldType}`, {
            defaultValue: field.fieldType,
          }),
        }),
        message: field.value,
      })),
    ...(latestValidation?.ruleValidations?.rules ?? [])
      .filter((rule) => !rule.passed)
      .map((rule) => ({ title: rule.ruleName, message: rule.message })),
  ];

  return (
    <SaveSectionsProvider>
      <div>
        {loading && <LoadingSpinner />}
        {error && (
          <Alert
            type="error"
            title={t("invoiceValidation.fetchInvoiceValidationError")}
            message={error?.toString()}
          />
        )}
        {clientError && (
          <Alert
            type="error"
            title={t("errors.couldNotFetchClient")}
            message={clientError?.toString()}
          />
        )}
        <div ref={containerRef}>
          <Row nogutter align="end" justify="between">
            <SecondaryPageTitleSection
              from={from}
              titleKey={invoice?.invoiceNumber ?? "invoices.invoice"}
              currentTitleKey="invoices.invoice"
            />
          </Row>
          <HorizontalSpacer spacing={1} />
          <Row nogutter justify="end" align="center" style={{ gap: 16 }}>
            <ToggleButton
              iconOn={<GridIcon color="white" />}
              iconOff={<GridIcon color={theme.colors.primary} />}
              isOn={isGridView}
              onToggle={() => setIsGridView(true)}
              size="small"
            />
            <ToggleButton
              iconOn={<ListIconView color="white" />}
              iconOff={<ListIconView color={theme.colors.primary} />}
              isOn={!isGridView}
              onToggle={() => setIsGridView(false)}
              size="small"
            />
            {id && invoiceProcessStatusIsInValidationStatus && (
              <RejectInvoicesButton
                type="single"
                buttonLabel={t("invoices.rejectInvoice")}
                invoiceId={id}
                fileId={invoice?.invoiceFileId}
                size="small"
              />
            )}

            {id && invoiceProcessStatusIsInValidationStatus && (
              <DeleteInvoicesButton
                buttonType="outlinedSmallDiscrete"
                invoiceIds={[id]}
              />
            )}
            <ActionButton
              color={theme.colors.primary}
              title={t("invoices.revalidate")}
              icon={<RefreshIcon color={theme.colors.white} />}
              disabled={!invoiceProcessStatusIsInValidationStatus}
              onClick={onClickRevalidate}
              loading={validateInvoiceLoading}
              small
            />
            {invoice?.latestValidation?.isValid &&
              invoice?.state?.validationStatus !==
                "VALIDATED_WITH_WARNINGS" && (
                <OutlinedButton
                  title={t("invoices.sendToAssessment")}
                  icon={<SendIcon />}
                  color={theme.colors.primary}
                  onClick={
                    invoice?.hasDuplicateInvoices
                      ? () => setSendToAssessmentsWithDupesOpen(true)
                      : onClickSendToAssessment
                  }
                  loading={sendToAssessmentLoading}
                  disabled={!invoiceProcessStatusIsInValidationStatus}
                />
              )}
            {invoice?.state?.validationStatus === "VALIDATED_WITH_WARNINGS" && (
              <OutlinedButton
                title={t("invoiceValidation.sendToAssessment")}
                icon={<SendIcon />}
                color={theme.colors.primary}
                onClick={() => setShowApproveWarningsModal(true)}
                loading={validateManuallyLoading}
                disabled={!invoiceProcessStatusIsInValidationStatus}
              />
            )}
          </Row>
          {sendToAssessmentError && (
            <Alert
              type="error"
              title={t("invoiceValidation.sendToAssessmentFailed")}
              message={sendToAssessmentError.toString()}
            />
          )}
          <HorizontalSpacer spacing={1} />
          <Card dontClipOverflow>
            <Row nogutter justify="between" align="center">
              <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
                <div
                  style={{
                    display: "flex",
                    gap: 16,
                    alignItems: "center",
                    flexWrap: "wrap",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {tags.map((tag) => (
                    <FilterChip
                      titleKey={`invoices.${tag.toLowerCase()}`}
                      selected
                      onSelect={() => onToggleTag(tag, false)}
                      addTag
                    />
                  ))}
                  {setTagsLoading && <SmallLoadingSpinner />}
                  <AddTagDropdown
                    tags={[
                      {
                        key: "AWAITING_ISSUER_RESPONSE",
                        labelKey: "invoices.awaitingResponseFromIssuer",
                        selected: tags.includes("AWAITING_ISSUER_RESPONSE"),
                      },
                      {
                        key: "AWAITING_REGISTRATION",
                        labelKey: "invoices.awaitingRegistration",
                        selected: tags.includes("AWAITING_REGISTRATION"),
                      },
                      {
                        key: "BUG_REPORTED",
                        labelKey: "invoices.bugReported",
                        selected: tags.includes("BUG_REPORTED"),
                      },
                    ]}
                    onToggle={(key) =>
                      onToggleTag(
                        key as InvoiceTag,
                        !tags.includes(key as InvoiceTag),
                      )
                    }
                  />
                </div>
                {invoice?.isCreditInvoice && (
                  <TextTag tagTitleKey="Kreditfaktura" variant="pink" />
                )}

                {invoice?.hasDuplicateInvoices && (
                  <DupeButtonContainer
                    onClick={() => {
                      setDuplicatedModalOpen(true);
                    }}
                  >
                    <TextTag tagTitleKey="DUPE" variant="warning" />
                  </DupeButtonContainer>
                )}
              </div>

              <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                {invoice?.id && (
                  <CopyForDebuggingButton
                    copyValue={invoice?.id}
                    textValue="Fakturaid"
                    variant="discrete"
                  />
                )}
                {clientIdentifier && (
                  <TextButton
                    title={t("clientCard.clientCard")}
                    color={theme.colors.primary}
                    onClick={(e) => {
                      e?.stopPropagation();
                      window.open(`/client/${clientIdentifier}`, "_blank");
                    }}
                    icon={<OpenLinkIconSmall color={theme.colors.primary} />}
                    iconPosition="right"
                  />
                )}
                {invoice?.id && clientIdentifier && (
                  <SendInvoiceMessageToClientButton
                    clientIdentifier={clientIdentifier}
                    invoiceIds={[invoice?.id]}
                    buttonType="outlinedSmallDiscrete"
                  />
                )}
              </div>
            </Row>
          </Card>

          <HorizontalSpacer spacing={1} />
          {(setTagsError ||
            errorMessage ||
            validateInvoiceError ||
            validateManuallyError) && (
            <Alert
              type="error"
              title={t("errors.somethingWentWrong")}
              message={
                setTagsError?.toString() ||
                errorMessage ||
                validateInvoiceError?.toString() ||
                validateManuallyError?.toString()
              }
            />
          )}
          <HorizontalSpacer spacing={1} />

          <div
            style={{
              display: "flex",
              gap: 24,
              flexDirection: isGridView ? "row" : "column",
              width: "100%",
            }}
          >
            <InformationDetailedColumn
              isGridView={isGridView}
              overview={{
                registered: invoice?.createdAt ?? new Date(),
                status: invoice?.state?.processStatus ?? "UNKNOWN",
                validationStatus: invoice?.state?.validationStatus,
              }}
              validationResult={invoice?.latestValidation}
              modifiedAt={invoice?.modifiedAt}
              clientIdentifier={clientIdentifier ?? "errorNoClientIdentifier"}
              invoiceId={invoice?.id ?? "errorNoInvoiceId"}
              ruleValidations={invoice?.latestValidation?.ruleValidations}
              documentRegistrationDetails={invoice?.uploadDocument}
              invoiceServiceType={invoice?.serviceType ?? "UNKNOWN"}
              isEditable={invoiceProcessStatusIsInValidationStatus}
              distributionType={invoice?.invoiceRecipient?.distributionType}
              externalErrorMessage={invoice?.externalErrorMessage}
              template={{
                templateName: invoice?.template?.name ?? "",
                templateId: invoice?.template?.id,
              }}
            />
            <InvoiceDetailsColumn
              isGridView={isGridView}
              validationResult={invoice?.latestValidation}
              issuerName={client?.name || ""}
              issuerClientIdentifier={invoice?.invoiceIssuer?.client?.id}
              issuerReference={invoice?.invoiceIssuer?.issuerReference || ""}
              dueDate={invoice?.dueDate}
              invoiceDate={invoice?.invoiceDate}
              invoiceRecipient={invoice?.invoiceRecipient}
              totalAmountWithVat={invoice?.totalAmountWithVat}
              invoiceNumber={invoice?.invoiceNumber || ""}
              totalVatAmount={invoice?.totalVatAmount}
              shippingCost={invoice?.shippingCost}
              shippingFeeVatPercentage={invoice?.shippingFeeVatPercentage}
              rounding={invoice?.rounding}
              invoiceRows={invoice?.rows}
              currencyCode={invoice?.currencyCode}
              isEditable={invoiceProcessStatusIsInValidationStatus}
              invoiceFee={invoice?.invoiceFee}
              isCreditInvoice={invoice?.isCreditInvoice}
              vatReverseCharge={invoice?.vatReverseCharge}
            />
            <div
              style={
                isGridView
                  ? { position: "sticky", top: 16, alignSelf: "flex-start" }
                  : undefined
              }
            >
              <InvoiceViewColumn
                fileId={invoice?.invoiceFileId || ""}
                isGridView={isGridView}
                collapsible
                templateId={invoice?.template?.id}
                clientIdentifier={clientIdentifier}
                noBorder
              />
            </div>
          </div>

          {showConfirmationModal && (
            <Modal
              closeIcon
              width={600}
              onClose={() => setShowConfirmationModal(false)}
              title={
                validationPassed
                  ? t("invoiceValidation.revalidationApproved")
                  : revalidationResultIsWarningsOnly
                    ? t("invoiceValidation.validatedWithWarnings")
                    : t("invoiceValidation.revalidationFailed")
              }
              actionButtons={{
                actionButton: invoiceHasBeenSentToAssessment
                  ? {
                      buttonTitle: t("invoiceValidation.goToAssessment"),
                      onClick: () => navigate(InvoiceAssessmentRoute.path),
                      loading: sendToAssessmentLoading,
                    }
                  : validationPassed
                    ? {
                        buttonTitle: t("invoiceValidation.sendToAssessment"),
                        onClick: invoice?.hasDuplicateInvoices
                          ? () => setSendToAssessmentsWithDupesOpen(true)
                          : onClickSendToAssessment,
                        loading: sendToAssessmentLoading,
                      }
                    : undefined,
                outlinedButton:
                  validationPassed && !invoiceHasBeenSentToAssessment
                    ? {
                        buttonTitle: t("invoiceValidation.leaveForValidation"),
                        onClick: () => setShowConfirmationModal(false),
                      }
                    : undefined,
                defaultButton: !validationPassed
                  ? {
                      buttonTitle: t("common.close"),
                      onClick: () => setShowConfirmationModal(false),
                    }
                  : undefined,
              }}
            >
              <HorizontalSpacer spacing={1} />
              <Row justify="center">
                {validationPassed ? (
                  <CheckedStatusCircle />
                ) : revalidationResultIsWarningsOnly ? (
                  <WarningStatusCircle />
                ) : (
                  <FailedStatusCircle />
                )}
              </Row>
              {invoiceHasBeenSentToAssessment ? (
                <div>
                  <HorizontalSpacer spacing={1} />

                  <Body>
                    {t("invoiceValidation.invoiceHasBeenSentForAssessment")}
                  </Body>
                  <HorizontalSpacer spacing={1} />
                </div>
              ) : (
                validationPassed && (
                  <>
                    <HorizontalSpacer spacing={1} />
                    <Body>
                      {t("invoiceValidation.revalidationApprovedMessage")}
                    </Body>
                    <HorizontalSpacer spacing={1} />
                  </>
                )
              )}
              {!validationPassed &&
                !invoiceHasBeenSentToAssessment &&
                !revalidationResultIsWarningsOnly &&
                failedValidationItems.length > 0 && (
                  <>
                    <HorizontalSpacer spacing={1} />
                    <Body>
                      {t("invoiceValidation.revalidationFailedReasons")}
                    </Body>
                    {failedValidationItems.map((item, index) => (
                      <CheckItemContainer key={index}>
                        <CircleIcon type="failed" />
                        <div>
                          <Body fontweight="600">{item.title}</Body>
                          {item.message && (
                            <BodySmall>{item.message}</BodySmall>
                          )}
                        </div>
                      </CheckItemContainer>
                    ))}
                    <HorizontalSpacer spacing={1} />
                  </>
                )}
            </Modal>
          )}
          {showApproveWarningsModal && (
            <Modal
              closeIcon
              width={600}
              onClose={() => setShowApproveWarningsModal(false)}
              title={t("invoiceValidation.approveWarnings")}
              actionButtons={{
                defaultButton: {
                  buttonTitle: t("common.cancel"),
                  onClick: () => setShowApproveWarningsModal(false),
                  disabled: validateManuallyLoading,
                },
                actionButton: {
                  buttonTitle: t("invoiceValidation.approveWarnings"),
                  onClick: onClickApproveWarnings,
                  loading: validateManuallyLoading,
                },
              }}
            >
              <HorizontalSpacer spacing={1} />
              <Body>{t("invoiceValidation.approveWarningsConfirmation")}</Body>
              <HorizontalSpacer spacing={1} />
              {invoice?.latestValidation?.ruleValidations?.rules
                ?.filter((rule) => rule.warning)
                .map((rule, index) => (
                  <CheckItemContainer key={index}>
                    <CircleIcon type="warning" />
                    <div>
                      <XS fontweight={400} color={theme.colors.textMuted}>
                        {t("invoiceValidation.rule").toUpperCase()}
                      </XS>
                      <Body fontweight="600">{rule.ruleName}</Body>
                      <BodySmall>{rule.message}</BodySmall>
                    </div>
                  </CheckItemContainer>
                ))}
              {invoice?.externalErrorMessage && (
                <CheckItemContainer>
                  <CircleIcon type="info" />
                  <div>
                    <XS fontweight={400} color={theme.colors.textMuted}>
                      {t(
                        "invoiceValidation.externalErrorMessage",
                      ).toUpperCase()}
                    </XS>
                    <BodySmall>{invoice.externalErrorMessage}</BodySmall>
                  </div>
                </CheckItemContainer>
              )}
            </Modal>
          )}
        </div>

        {clientIdentifier && invoice?.id && duplicatedModalOpen && (
          <Modal
            onClose={() => {
              setDuplicatedModalOpen(false);
            }}
            closeIcon
            width={1250}
          >
            <DuplicatedInvoiceNumberListModalContent
              clientIdentifier={clientIdentifier}
              invoiceId={invoice?.id}
            />
          </Modal>
        )}

        <HorizontalSpacer spacing={4} />

        {invoice?.id && invoiceProcessStatusIsInValidationStatus && (
          <ValidationActionBottomBar
            invoiceId={invoice.id}
            clientIdentifier={clientIdentifier ?? "Missing client identifier"}
            invoiceTags={tags}
            isValid={invoice.latestValidation?.isValid ?? false}
            onRevalidate={onClickRevalidate}
            revalidateLoading={validateInvoiceLoading}
            onSendToAssessment={
              invoice?.hasDuplicateInvoices
                ? () => setSendToAssessmentsWithDupesOpen(true)
                : onClickSendToAssessment
            }
            sendToAssessmentLoading={sendToAssessmentLoading}
            onApplyTags={onApplyTags}
          />
        )}
      </div>

      {sendToAssessmentWithDupesOpen && clientIdentifier && (
        <SendValidationInvoiceToAssessmentWithDupesModal
          clientIdentifier={clientIdentifier}
          invoiceId={invoice?.id ?? ""}
          onClickSendToAssessment={onClickSendToAssessment}
          onCancel={() => setSendToAssessmentsWithDupesOpen(false)}
        />
      )}
    </SaveSectionsProvider>
  );
};

export default InvoiceValidationDetailedPage;
